import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { formatISO } from 'date-fns';
import { useMemo, useState, type ReactNode } from 'react';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getSortableEventId } from '../utils/get-sortable-event-id.ts';
import { DaySuggestions } from './day-suggestions.tsx';
import { EventsLoadingState } from './events-loading-state.tsx';
import { OverlayEventCard } from './overlay-event-card.tsx';
import { SortableEventCard } from './sortable-event-card.tsx';

export type Props = {
    day: string;
    ownerId: string;
};

export function EventsOverviewContentDay({ day, ownerId }: Props): ReactNode {
    const apiUtils = api.useUtils();
    const today = formatISO(new Date(), { representation: 'date' });
    const params = { dateFrom: day, dateTo: day, ownerId };
    const { data, isLoading } = api.platoUser.query.events.getList.useQuery(params);
    const [activeId, setActiveId] = useState<string | null>(null);
    const sendEvent = useAnalytics();

    const reorderEvents = api.platoUser.command.events.reorder.useMutation({
        onSettled: () => {
            void apiUtils.platoUser.query.events.getList.invalidate();
        },
    });

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    );

    const sortedIds = useMemo(() => data?.map((event) => getSortableEventId(event)) ?? [], [data]);

    const handleDragStart = (event: DragStartEvent): void => {
        setActiveId(event.active.id.toString());
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortedIds.indexOf(active.id.toString());
            const newIndex = sortedIds.indexOf(over.id.toString());

            const events_order = arrayMove(sortedIds, oldIndex, newIndex);

            void apiUtils.platoUser.query.events.getList.cancel(params);

            const oldItems = apiUtils.platoUser.query.events.getList.getData(params) ?? [];
            const newItems: typeof oldItems = [];

            for (const event_id of events_order) {
                const oldItem = oldItems.find((event) => getSortableEventId(event) === event_id);
                if (oldItem) {
                    newItems.push(oldItem);
                }
            }

            apiUtils.platoUser.query.events.getList.setData(params, newItems);

            // Track event reordering
            const reorderedEvent = oldItems.find((event) => getSortableEventId(event) === active.id);
            if (reorderedEvent) {
                sendEvent('event_reordered', getEventMetadata(reorderedEvent));
            }

            reorderEvents.mutate({
                events_order: newItems.map((event) => event.event_id),
                date: day,
            });
        }

        setActiveId(null);
    };

    if (isLoading) {
        return <EventsLoadingState />;
    }

    const events = data ?? [];
    const activeEvent = events.find((event) => getSortableEventId(event) === activeId);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
        >
            <div className="max-w-xl mx-auto">
                <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3 pb-3">
                        {events.map((event) => (
                            <SortableEventCard key={event.event_id} event={event} date={day} />
                        ))}
                    </div>
                </SortableContext>
                {/* Suggestions are anchored on the day's visits; without any, there is no route to anchor on.
                    Past days can no longer be planned. ISO date strings compare correctly as strings. */}
                {events.length > 0 && day >= today && (
                    <>
                        <div className="h-3" />
                        <DaySuggestions day={day} ownerId={ownerId} />
                    </>
                )}
            </div>

            <DragOverlay>
                {activeEvent ? <OverlayEventCard event={activeEvent} date={day} /> : null}
            </DragOverlay>
        </DndContext>
    );
}
